import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ServicePriceDialog from './ServicePriceDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { getServicePrices, deleteServicePrice } from './servicePriceService';

interface ServicePrice {
  id: number;
  appointment_type_id: string;
  appointment_type_name: string;
  price: number | null;
  custom_description: string | null;
  created_at: string;
  updated_at: string;
}

const ServicePriceManager: React.FC = () => {
  const [prices, setPrices] = useState<ServicePrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState<ServicePrice | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const fetchPrices = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getServicePrices();
      setPrices(data.prices || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load service prices');
      console.error('Error loading service prices:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPrices();
  }, []);
  
  const handleAddClick = () => {
    setSelectedPrice(null);
    setOpenDialog(true);
  };
  
  const handleEditClick = (price: ServicePrice) => {
    setSelectedPrice(price);
    setOpenDialog(true);
  };
  
  const handleDeleteClick = (price: ServicePrice) => {
    setSelectedPrice(price);
    setOpenDeleteDialog(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!selectedPrice) return;
    
    try {
      setLoading(true);
      await deleteServicePrice(selectedPrice.id);
      setOpenDeleteDialog(false);
      await fetchPrices(); // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to delete service price');
      console.error('Error deleting service price:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDialogClose = (refreshNeeded: boolean) => {
    setOpenDialog(false);
    if (refreshNeeded) {
      fetchPrices();
    }
  };
  
  const formatPrice = (price: number | null) => {
    if (price === null) return 'Not set';
    if (typeof price !== 'number') return `$${price}`;
    return `$${price.toFixed(2)}`;
  };
  
  // Filter prices based on search term
  const filteredPrices = prices.filter(price =>
    price.appointment_type_name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Service Price Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add Price
        </Button>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <TextField
          label="Search by service name"
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {loading && !prices.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Service Name</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Custom Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPrices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {searchTerm ? 'No matching services found.' : 'No service prices configured. Click "Add Price" to create one.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPrices.map((price) => (
                  <TableRow key={price.id}>
                    <TableCell>{price.appointment_type_name}</TableCell>
                    <TableCell>{formatPrice(price.price)}</TableCell>
                    <TableCell>
                      {price.custom_description ? 
                        (price.custom_description.length > 50 ? 
                          `${price.custom_description.substring(0, 50)}...` : 
                          price.custom_description) : 
                        'None'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(price)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(price)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {openDialog && (
        <ServicePriceDialog
          open={openDialog}
          onClose={handleDialogClose}
          price={selectedPrice}
        />
      )}
      
      {openDeleteDialog && selectedPrice && (
        <DeleteConfirmationDialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirm={handleDeleteConfirm}
          serviceName={selectedPrice.appointment_type_name}
        />
      )}
    </Box>
  );
};

export default ServicePriceManager;